<template>
  <div class="vta-reporte">

    <!-- Encabezado -->
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <h5 class="vta-titulo mb-0">
        <i class="bi bi-bar-chart-line me-2"></i>Ventas
      </h5>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span v-if="reporte" class="vta-periodo-badge">{{ reporte.periodo.etiqueta }}</span>
        <template v-if="reporte && !cargando">
          <button
            class="btn btn-sm btn-outline-success"
            @click="exportarXlsx"
            :disabled="exportandoXlsx"
            title="Descargar Excel"
          >
            <span v-if="exportandoXlsx" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-file-earmark-excel me-1"></i>Excel
          </button>
          <button
            class="btn btn-sm btn-outline-danger"
            @click="exportarPdf"
            :disabled="exportandoPdf"
            title="Descargar PDF"
          >
            <span v-if="exportandoPdf" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-file-earmark-pdf me-1"></i>PDF
          </button>
        </template>
      </div>
    </div>

    <!-- Panel filtros -->
    <div class="vta-panel mb-3">
      <div class="vta-panel__cuerpo">

        <!-- Toggle modo -->
        <div class="d-flex gap-2 mb-3">
          <button
            :class="['btn btn-sm', modoFiltro === 'anio' ? 'btn-primary' : 'btn-outline-secondary']"
            @click="modoFiltro = 'anio'"
          ><i class="bi bi-calendar2-range me-1"></i>Por año</button>
          <button
            :class="['btn btn-sm', modoFiltro === 'rango' ? 'btn-primary' : 'btn-outline-secondary']"
            @click="modoFiltro = 'rango'"
          ><i class="bi bi-calendar3 me-1"></i>Rango de fechas</button>
        </div>

        <!-- Selector por año -->
        <div v-if="modoFiltro === 'anio'" class="d-flex flex-wrap gap-2 align-items-center">
          <span class="vta-label-muted me-1">Seleccionar año:</span>
          <button
            v-for="a in aniosDisponibles"
            :key="a"
            :class="['btn btn-sm', anioSeleccionado === a ? 'btn-primary' : 'btn-outline-secondary']"
            @click="seleccionarAnio(a)"
            :disabled="cargando"
          >{{ a }}</button>
        </div>

        <!-- Rango personalizado -->
        <div v-if="modoFiltro === 'rango'" class="row g-2 align-items-end">
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Fecha desde</label>
            <input type="date" class="form-control form-control-sm vta-input" v-model="filtros.fecha_desde" :max="filtros.fecha_hasta" />
          </div>
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Fecha hasta</label>
            <input type="date" class="form-control form-control-sm vta-input" v-model="filtros.fecha_hasta" :min="filtros.fecha_desde" />
          </div>
          <div class="col-auto d-flex gap-1">
            <button class="btn btn-sm btn-primary" @click="generarReporte" :disabled="cargando">
              <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-search me-1"></i>{{ cargando ? 'Generando…' : 'Generar' }}
            </button>
            <button class="btn btn-sm btn-link" style="color:var(--text-muted)" @click="limpiarRango" :disabled="cargando">
              Limpiar
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMensaje" class="vta-alerta vta-alerta--error d-flex align-items-center gap-2 mb-3">
      <i class="bi bi-x-circle-fill"></i>
      <span>{{ errorMensaje }}</span>
    </div>

    <!-- Cargando -->
    <div v-if="cargando" class="vta-estado-vacio">
      <span class="spinner-border spinner-border-sm me-2"></span>Generando reporte…
    </div>

    <template v-if="reporte && !cargando">

      <!-- KPI cards -->
      <div class="row g-3 mb-3">
        <div class="col-sm-6 col-xl-3">
          <div class="vta-kpi">
            <div class="vta-kpi__valor" style="color:var(--primary-color)">{{ reporte.resumen.num_facturas }}</div>
            <div class="vta-kpi__etiqueta">Facturas emitidas</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl-3">
          <div class="vta-kpi">
            <div class="vta-kpi__valor" style="color:var(--primary-color)">{{ formatearMoneda(reporte.resumen.subtotal) }}</div>
            <div class="vta-kpi__etiqueta">Subtotal sin IVA</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl-3">
          <div class="vta-kpi">
            <div class="vta-kpi__valor" style="color:var(--accent-warning)">{{ formatearMoneda(reporte.resumen.iva) }}</div>
            <div class="vta-kpi__etiqueta">IVA cobrado</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl-3">
          <div class="vta-kpi">
            <div class="vta-kpi__valor" style="color:var(--accent-success)">{{ formatearMoneda(reporte.resumen.total) }}</div>
            <div class="vta-kpi__etiqueta">Total con IVA</div>
          </div>
        </div>
      </div>

      <!-- Resumen por año -->
      <div v-if="reporte.resumen_anual.length > 0" class="vta-panel mb-3">
        <div class="vta-panel__cabecera">
          <i class="bi bi-bar-chart me-2"></i>Resumen por año
        </div>
        <div class="table-responsive">
          <table class="vta-tabla">
            <thead>
              <tr>
                <th>Año</th>
                <th class="text-end">Facturas</th>
                <th class="text-end">Subtotal</th>
                <th class="text-end">IVA</th>
                <th class="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="fila in reporte.resumen_anual"
                :key="fila.anio"
                class="vta-fila"
                :class="{ 'vta-fila--activa': esFilaActiva(fila.anio) }"
              >
                <td>
                  <span class="fw-semibold">{{ fila.anio }}</span>
                  <span v-if="esFilaActiva(fila.anio)" class="vta-badge-sel ms-1">seleccionado</span>
                </td>
                <td class="text-end">{{ fila.num_facturas }}</td>
                <td class="text-end">{{ formatearMoneda(fila.subtotal) }}</td>
                <td class="text-end vta-muted">{{ formatearMoneda(fila.iva) }}</td>
                <td class="text-end fw-semibold" style="color:var(--accent-success)">{{ formatearMoneda(fila.total) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Tabla de facturas -->
      <div class="vta-panel mb-3">
        <div class="vta-panel__cabecera d-flex align-items-center justify-content-between">
          <span><i class="bi bi-receipt me-2"></i>Facturas autorizadas — {{ reporte.periodo.etiqueta }}</span>
          <span class="vta-conteo">{{ reporte.facturas.length }} factura(s)</span>
        </div>
        <div v-if="reporte.facturas.length > 0" class="table-responsive">
          <table class="vta-tabla">
            <thead>
              <tr>
                <th style="width:38px">#</th>
                <th>Fecha</th>
                <th>N.° Factura</th>
                <th>Cliente</th>
                <th>Identificación</th>
                <th class="text-end">Subtotal</th>
                <th class="text-end">IVA</th>
                <th class="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(f, idx) in reporte.facturas"
                :key="f.id"
                class="vta-fila"
              >
                <td class="text-center vta-muted">{{ idx + 1 }}</td>
                <td>{{ f.fecha_emision }}</td>
                <td class="fw-semibold" style="font-family:monospace">{{ f.numero_factura }}</td>
                <td>{{ f.cliente }}</td>
                <td class="vta-muted">{{ f.identificacion }}</td>
                <td class="text-end">{{ formatearMoneda(f.subtotal) }}</td>
                <td class="text-end vta-muted">{{ formatearMoneda(f.iva) }}</td>
                <td class="text-end fw-semibold" style="color:var(--accent-success)">{{ formatearMoneda(f.total) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="vta-pie">
                <td colspan="5">Total</td>
                <td class="text-end">{{ formatearMoneda(reporte.resumen.subtotal) }}</td>
                <td class="text-end vta-muted">{{ formatearMoneda(reporte.resumen.iva) }}</td>
                <td class="text-end" style="color:var(--accent-success)">{{ formatearMoneda(reporte.resumen.total) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="vta-estado-vacio">
          <i class="bi bi-receipt fs-3 d-block mb-2"></i>
          No hay facturas autorizadas en este período.
        </div>
      </div>

    </template>

    <!-- Estado inicial -->
    <div v-if="!reporte && !cargando && !errorMensaje" class="vta-panel">
      <div class="vta-estado-vacio">
        <i class="bi bi-bar-chart-line fs-2 d-block mb-2"></i>
        Selecciona un año o rango de fechas para generar el reporte.
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'
import * as XLSX from 'xlsx'

export default {
  name: 'ClientReportesVentas',

  data() {
    const hoy = new Date()
    return {
      modoFiltro: 'anio',
      anioSeleccionado: hoy.getFullYear(),
      filtros: {
        fecha_desde: `${hoy.getFullYear()}-01-01`,
        fecha_hasta: hoy.toISOString().slice(0, 10),
      },
      cargando: false,
      errorMensaje: null,
      reporte: null,
      exportandoXlsx: false,
      exportandoPdf: false,
    }
  },

  mounted() {
    this.generarReporte()
  },

  computed: {
    themeClass() {
      return themeManager.themeClass
    },

    aniosDisponibles() {
      const actual = new Date().getFullYear()
      const set = new Set([actual])
      if (this.reporte?.resumen_anual) {
        this.reporte.resumen_anual.forEach(r => set.add(Number(r.anio)))
      }
      return Array.from(set).sort((a, b) => b - a)
    },
  },

  methods: {
    async generarReporte() {
      this.cargando = true
      this.errorMensaje = null
      this.reporte = null
      try {
        const params = new URLSearchParams()
        if (this.modoFiltro === 'anio') {
          params.set('anio', this.anioSeleccionado)
        } else {
          if (this.filtros.fecha_desde) params.set('fecha_desde', this.filtros.fecha_desde)
          if (this.filtros.fecha_hasta) params.set('fecha_hasta', this.filtros.fecha_hasta)
        }
        const { data } = await api.get(`/reportes/ventas?${params}`)
        if (data.success) {
          this.reporte = data
        } else {
          this.errorMensaje = data.error || 'No se pudieron obtener los datos.'
        }
      } catch (err) {
        this.errorMensaje = err?.response?.data?.error || 'Error al conectar con el servidor.'
      } finally {
        this.cargando = false
      }
    },

    seleccionarAnio(anio) {
      this.anioSeleccionado = anio
      this.generarReporte()
    },

    limpiarRango() {
      const hoy = new Date()
      this.filtros.fecha_desde = `${hoy.getFullYear()}-01-01`
      this.filtros.fecha_hasta = hoy.toISOString().slice(0, 10)
      this.reporte = null
      this.errorMensaje = null
    },

    esFilaActiva(anio) {
      return this.modoFiltro === 'anio' && Number(anio) === this.anioSeleccionado
    },

    formatearMoneda(valor) {
      return '$' + parseFloat(valor || 0).toFixed(2)
    },

    // ── Exportar Excel ────────────────────────────────────────────────
    exportarXlsx() {
      if (!this.reporte) return
      this.exportandoXlsx = true
      try {
        const r  = this.reporte
        const wb = XLSX.utils.book_new()

        // Hoja Resumen
        const resumen = [
          ['Reporte de Ventas'],
          ['Período', r.periodo.etiqueta],
          [],
          ['Concepto', 'Valor'],
          ['Facturas emitidas', r.resumen.num_facturas],
          ['Subtotal sin IVA',  r.resumen.subtotal],
          ['IVA cobrado',       r.resumen.iva],
          ['Total con IVA',     r.resumen.total],
        ]
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumen), 'Resumen')

        // Hoja por año
        if (r.resumen_anual.length > 0) {
          const cabA = ['Año', 'Facturas', 'Subtotal', 'IVA', 'Total']
          const filA = r.resumen_anual.map(f => [
            f.anio, f.num_facturas,
            parseFloat(f.subtotal), parseFloat(f.iva), parseFloat(f.total),
          ])
          XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cabA, ...filA]), 'Por año')
        }

        // Hoja facturas
        const cabF = ['Fecha', 'N.° Factura', 'Cliente', 'Identificación', 'Subtotal', 'IVA', 'Total']
        const filF = r.facturas.map(f => [
          f.fecha_emision, f.numero_factura, f.cliente, f.identificacion,
          parseFloat(f.subtotal), parseFloat(f.iva), parseFloat(f.total),
        ])
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cabF, ...filF]), 'Facturas')

        const etiq = r.periodo.etiqueta.replace(/[^a-z0-9]/gi, '_')
        XLSX.writeFile(wb, `ventas_${etiq}.xlsx`)
      } finally {
        this.exportandoXlsx = false
      }
    },

    // ── Exportar PDF ──────────────────────────────────────────────────
    async exportarPdf() {
      if (!this.reporte) return
      this.exportandoPdf = true
      try {
        const params = new URLSearchParams()
        if (this.modoFiltro === 'anio') {
          params.set('anio', this.anioSeleccionado)
        } else {
          if (this.filtros.fecha_desde) params.set('fecha_desde', this.filtros.fecha_desde)
          if (this.filtros.fecha_hasta) params.set('fecha_hasta', this.filtros.fecha_hasta)
        }
        const response = await api.get(
          `/reportes/ventas/pdf?${params}`,
          { responseType: 'blob' }
        )
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url  = URL.createObjectURL(blob)
        const a    = document.createElement('a')
        const etiq = this.reporte.periodo.etiqueta.replace(/[^a-z0-9]/gi, '_')
        a.href     = url
        a.download = `ventas_${etiq}.pdf`
        a.click()
        URL.revokeObjectURL(url)
      } catch {
        this.errorMensaje = 'No se pudo generar el PDF. Intente nuevamente.'
      } finally {
        this.exportandoPdf = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Contenedor ── */
.vta-reporte {
  padding: 1.25rem;
}

.vta-titulo {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--primary-color);
}

.vta-periodo-badge {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--secondary-bg-soft);
  border: 1px solid var(--border-main);
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
}

/* ── Panel ── */
.vta-panel {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.vta-panel__cuerpo {
  padding: 0.85rem 1rem;
}

.vta-panel__cabecera {
  padding: 0.6rem 0.85rem;
  background: var(--secondary-bg-soft);
  border-bottom: 1px solid var(--border-main);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
}

/* ── KPI ── */
.vta-kpi {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  text-align: center;
  padding: 1rem;
}

.vta-kpi__valor {
  font-size: 1.5rem;
  font-weight: 700;
}

.vta-kpi__etiqueta {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
}

/* ── Alertas ── */
.vta-alerta {
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  font-size: 0.875rem;
  border: 1px solid;
}

.vta-alerta--error {
  background: var(--accent-danger-light);
  border-color: var(--accent-danger);
  color: var(--accent-danger);
}

/* ── Estado vacío ── */
.vta-estado-vacio {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  font-size: 0.9rem;
}

/* ── Tabla ── */
.vta-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.vta-tabla thead tr {
  background: var(--secondary-bg-soft);
  border-bottom: 2px solid var(--border-main);
}

.vta-tabla thead th {
  padding: 0.6rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.vta-fila {
  border-bottom: 1px solid var(--border-main);
  transition: background 0.1s;
}

.vta-fila td {
  padding: 0.55rem 0.75rem;
  vertical-align: middle;
  color: var(--text-primary);
}

.vta-fila:hover {
  background: var(--table-hover);
}

.vta-fila--activa {
  background: var(--accent-info-light) !important;
  border-left: 3px solid var(--primary-color);
}

/* ── Pie tabla ── */
.vta-pie td {
  padding: 0.55rem 0.75rem;
  font-weight: 600;
  color: var(--text-primary);
  background: var(--secondary-bg-soft);
  border-top: 2px solid var(--border-main);
}

/* ── Auxiliares ── */
.vta-muted {
  color: var(--text-muted);
  font-size: 0.82rem;
}

.vta-label-muted {
  font-size: 0.82rem;
  color: var(--text-muted);
}

.vta-conteo {
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  background: var(--primary-color);
  border-radius: 20px;
  padding: 0.2rem 0.6rem;
}

.vta-badge-sel {
  font-size: 0.7rem;
  font-weight: 600;
  color: #fff;
  background: var(--primary-color);
  border-radius: 10px;
  padding: 0.1rem 0.45rem;
}

/* ── Input ── */
.vta-input {
  background: var(--input-bg);
  color: var(--text-primary);
  border-color: var(--border-main);
}

.vta-input:focus {
  border-color: var(--border-focus);
  box-shadow: none;
}
</style>
